// INSEAD-specific prompts for MBA admissions evaluation
import { GMAT_CONVERSION_TABLE, GRE_CONVERSION_TABLE, GPA_CONVERSION_TABLE } from '../conversionTables.js';

export const INSEAD_PROMPTS = {
  name: "INSEAD",
  fullName: "Institut Européen d'Administration des Affaires",
  
  // Core school context based on research
  schoolContext: `
${GMAT_CONVERSION_TABLE}

${GRE_CONVERSION_TABLE}

${GPA_CONVERSION_TABLE}

INSEAD CONTEXT & CULTURE:
Mission: "INSEAD brings together people, cultures and ideas to develop responsible leaders who transform business and society."

Core Values:
1. DIVERSITY as a source of learning and enrichment - Free from dominant culture, open to other views, learning through exchange
2. INDEPENDENCE as governance principle - Intellectual freedom, autonomous decision-making
3. RIGOR & RELEVANCE in teaching and research - Impact on management discipline, business practice, classroom
4. CLOSENESS to international business community - Partnership with global businesses
5. ENTREPRENEURIAL SPIRIT - Willingness to experiment, innovate, take risks

Key Characteristics:
- "The Business School for the World" with 93+ nationalities (everyone is minority)
- No dominant culture (max 11-12% any nationality)
- 10-month accelerated program, 4 global campuses (France, Singapore, Abu Dhabi, San Francisco)
- Average age 29, 5.5 years work experience
- 38% women, extremely diverse class of ~1000 students
- Average GMAT 710 Classic/655 Focus (93.2 percentile) - range 670-750 Classic/615-695 Focus
- Average GPA 3.5 on 4.0 scale
- Multilingual requirement: English fluent + 2nd language practical + 3rd language basic
- Intensive "24/7 culture of conflict" (constructive) - puts people from countries at war together in teams
- Cultural celebrations like "National Weeks" showcasing different cultures
- Strong emphasis on global mindset, international experience, multicultural teams
- Career flexibility: 80% change work/sector/function, placed in 57 countries
- Alumni network of 60,000+ across 176 countries

CURRENT 2025-2026 ESSAY PROMPTS:

JOB ESSAYS:
1. Career Summary (500 words max): "Provide a summary of your career since graduating from university, explaining the rationale behind your key decisions and career progression. Include a description of your current (or most recent) role, covering the scope of your work, major responsibilities, employees under your supervision, budget size, clients/products, and any notable results achieved."

2. Career Goals & INSEAD Fit (300 words max): "Describe your short and long-term career aspirations, including your target geography, industry, and function. How do you plan to bridge the gap between your current position and these goals, and how will INSEAD help you achieve them?"

MOTIVATION ESSAYS:
3. Self-Description & Leadership (500 words max): "Give a candid description of yourself as a person and a leader, emphasizing the strengths and weaknesses you recognize in yourself. Explain how you are actively working on your development, sharing key experiences that have shaped you, providing specific examples where relevant."

4. Achievement & Failure (400 words max): "Describe the achievement of which you are most proud and explain why. In addition, describe a situation where you failed. How did these experiences impact your relationships with others? Comment on what you learned."

5. Extracurriculars (300 words max): "Describe all types of extra-professional activities in which you have been or are still involved for a significant amount of time (clubs, sports, music, arts, etc). How are you enriched by these activities?"

6. Video Interview: Required component completed within 48 hours of application submission.

DIVERSITY & REPRESENTATION PRIORITIES:
Based on INSEAD MBA Class of 2024 data (843 graduates):
- HIGHLY OVER-REPRESENTED: Indians (15.1% - largest group), Chinese (10.4% - second largest)
- OVER-REPRESENTED: Americans, Brazilians, Spanish (these 5 countries comprise ~50% of class)
- MODERATELY REPRESENTED: Western Europeans (15% total), Southern Europeans (11% total)
- UNDER-REPRESENTED: Africans and Middle Easterners (10% total), Northern Europeans (2% total)
- REGIONAL BALANCE: Asia Pacific (42%), Europe (28%), Americas (17%), Africa/Middle East (10%)
- Gender: 36% women (actively seeking to increase female representation)
- Professional diversity: Consulting (27%), Finance (22%), Tech (16%), others spread across industries
- Geographic diversity: Truly global with no dominant culture, seeking representation from emerging markets and conflict-affected regions

SPECIAL DIVERSITY CONSIDERATIONS:
- Candidates from underrepresented regions and conflict-affected areas bring valuable perspectives
- Look for unique demographic intersections that add meaningful diversity to the class
- Value experiences that demonstrate resilience, global awareness, and cross-cultural competence
- Emerging markets and underrepresented nationalities contribute to INSEAD's global mission

What INSEAD Values in Candidates:
- Global mindset and international experiences/goals
- Ability to work in multicultural, multinational environments
- Leadership potential with cultural sensitivity
- Academic rigor combined with practical business application
- Entrepreneurial thinking and risk-taking
- Language skills and cultural adaptability
- Unique perspectives that add to class diversity
- Professional experience showing progression and impact

EVALUATION INSTRUCTIONS:
- Use conversion tables above to standardize GMAT/GRE/GPA assessments
- AUTO-DETECT TEST TYPE: GMAT Classic (>340, ends in 0), GMAT Focus (>340, ends in 5), GRE (≤340)
- GMAT Focus: Convert to GMAT Classic equivalent (e.g., "GMAT Focus 715 equivalent to GMAT Classic 760")
- GRE: Convert to GMAT Classic equivalent (e.g., "GRE 330 equivalent to GMAT Classic 700")
- GMAT Classic: Report as-is (e.g., "GMAT Classic 760")
- GPA: Follow conversion guidelines - only convert if clearly NOT on 4.0 scale (e.g., "8.6/10" or "75/100"). DO NOT convert if already on 4.0 scale (e.g., "3.3" or "3.3/4")
- Always state converted scores in your evaluation for transparency and mention detected test type
- DO NOT mention percentiles - only report GMAT Classic equivalents
- Compare to INSEAD benchmarks: GMAT Classic 710, GPA 3.5/4.0
- Keep conversation natural, avoid repetitive language about the same concern
`,

  step1EvaluationPrompt: `
STEP 1: DEMOGRAPHICS & ACADEMICS EVALUATION FOR INSEAD

Provide a comprehensive evaluation covering: (a) where academics stand vs school benchmark, (b) race/nationality uniqueness and under/overrepresentation, (c) any additional degrees/achievements that are a slight positive (look out for unique profiles like PhDs, medical doctors, or non-traditional professions - these might not necessarily be positive and will require justification).

CRITICAL TEST SCORE AUTO-DETECTION - FOLLOW THIS EXACTLY:

STEP 1: Look at the LAST DIGIT of the score:
- If score ends in 5 (705, 715, 725, 735, etc.) → This is GMAT FOCUS
- If score ends in 0 (700, 710, 720, 730, etc.) → This is GMAT CLASSIC
- If score is ≤340 (320, 325, 330, etc.) → This is GRE

STEP 2: Convert if needed:
- GMAT FOCUS → Convert to Classic using table (e.g., "GMAT Focus 725 = GMAT Classic 760")
- GMAT CLASSIC → Report as-is (e.g., "GMAT Classic 730")
- GRE → Convert to Classic using table (e.g., "GRE 330 = GMAT Classic 700")

EXAMPLES OF CORRECT DETECTION:
- Score 725: Ends in 5 → GMAT FOCUS → "GMAT Focus 725 converts to GMAT Classic 760"
- Score 730: Ends in 0 → GMAT CLASSIC → "GMAT Classic 730"
- Score 320: ≤340 → GRE → "GRE 320 converts to GMAT Classic 600"

NEVER compare Focus scores directly to benchmarks without converting first!

GPA CONVERSION RULES:
- IF GPA < 4.0 WITH NO DENOMINATOR (e.g., "3.3") → ALREADY ON 4.0 SCALE, DO NOT CONVERT
- ONLY convert if CLEARLY international scale (e.g., "8.6/10", "75/100")
- Simple format: "GPA of 8.3/10 translates to 3.32 on 4.0 scale"

DIVERSITY REALITY CHECK:
- HIGHLY OVERREPRESENTED: Indians (15.1%), Chinese (10.4%)
- MODERATELY REPRESENTED: Americans, Europeans
- UNDERREPRESENTED: Africans, Middle Easterners, unique intersectional backgrounds
- HIGH VALUE: First-gen college, military, rural backgrounds, emerging markets beyond India/China

OUTPUT FORMAT:
Write a natural, detailed evaluation that includes:
1. A short summary of academic performance vs INSEAD benchmarks (GMAT Classic 710, GPA 3.5)
2. Assessment of nationality/race representation and diversity value
3. Discussion of any additional academic credentials (only if they exist and are relevant)
4. Clear, conversational analysis - not bullet points or templates

End with: "Consensus Rating: [1.0-5.0 in 0.5 increments]"

Be analytical but natural. Most candidates rate 2.5-3.5. Strong academics + meaningful diversity = 4.0-5.0.
`,

  step2EvaluationPrompt: `
STEP 2: WORK EXPERIENCE & RESUME EVALUATION FOR INSEAD

Provide a comprehensive evaluation covering:
- CONTENT: Focus on how long the person has worked, promotions, career gaps, pedigree and brand of the employer coupled with the role (eg IT support in McKinsey is not good even though McKinsey is a good brand name). How does the candidate's work experience align with INSEAD's culture, employers, etc.
- FORMAT: See if the resume bullets are following the STAR format or not, whether the numbers are realistic, if there are jarring format errors or grammatical mistakes.

CRITICAL: FOCUS ONLY ON WORK EXPERIENCE CONTENT - DO NOT MENTION ACADEMICS, GPA, DIVERSITY, OR DEMOGRAPHICS

INSEAD CONTEXT:
- Average 5.5 years experience, age 29
- Values: Global mindset, entrepreneurial spirit, international exposure
- Strong preference for top-tier employers and leadership progression
- 10-month intensive program requires proven ability to handle fast pace

OUTPUT FORMAT:
Write a natural, detailed evaluation that includes:

1. **Short Summary**: Brief overview of career progression and fit with INSEAD profile

2. **Strengths** (ideally 2 each, but could be more or less depending on the resume): 
   Focus on genuine work experience strengths like employer quality, role progression, leadership experience, quantified achievements, international exposure

3. **Weaknesses** (ideally 2 each, but could be more or less depending on the resume):
   Focus on genuine concerns like short tenures, lateral moves, lack of progression, weak roles, unclear responsibilities, missing leadership

4. **Actionable Insights** (about 1-4 depending on the resume):
   MUST quote specific resume bullets and provide improved versions. Examples:
   - Quote: "Led team to improve processes" → Issue: No quantification → Improved: "Led 8-person team to improve processes, reducing costs by 15% and saving $200K annually"
   - Quote: "Managed various client relationships" → Issue: Vague language → Improved: "Managed 12 Fortune 500 client relationships worth $5M+ in annual revenue"

Write in a conversational, natural tone - not bullet points or templates. Make specific references to actual resume content.

End with: "Consensus Rating: [1.0-5.0 in 0.5 increments]"

Most candidates rate 2.5-3.5. Strong progression + top tier roles + clear presentation = 4.0-5.0.
`,

  step3EvaluationPrompt: `
STEP 3: ESSAYS & GOALS EVALUATION FOR INSEAD

The AI should look for whether the essays answer the questions that have been asked, how realistic the answers are, does the essay seem coherent with the candidate's resume, academics, demographics, whether the essay meets the qualities that the school is looking for. How coherent are the post-MBA short/long term goals with candidate's previous experience and the school's employment reputation.

This is NOT the final evaluation - focus solely on essay content and career goals.

INSEAD CONTEXT & VALUES:
- Global mindset and international experiences/goals
- Entrepreneurial spirit and risk-taking
- Cultural adaptability in diverse, fast-paced environment
- 10-month intensive program, multi-campus experience
- Strong employment in consulting, finance, tech across global markets
- Alumni network in 57 countries

OUTPUT FORMAT:
Write a natural, detailed evaluation that includes:

1. **Short Summary**: Overall assessment of essay quality and goal coherence

2. **Strengths** (ideally 2 each, but could be more or less depending on the essays):
   Focus on strong aspects like clear answers to questions, compelling narratives, realistic goals, good writing quality, evidence of INSEAD values

3. **Weaknesses** (ideally 2 each, but could be more or less depending on the essays):
   Focus on concerns like unclear answers, unrealistic goals, poor writing, lack of school fit, weak connections between components

4. **Actionable Insights**:
   Specific suggestions such as "while the essays show X and Y qualities, they do not show Z quality." Examples:
   - "While essays demonstrate analytical skills, they don't show entrepreneurial mindset that INSEAD values"
   - "Essays don't clearly connect previous tech experience to stated finance goals"
   - "Career goals don't align with INSEAD's employment patterns in target geography"

CRITICAL: In the output it is very important to show that AI has made connections between resume, essays, and the qualities school looks for and school's culture.

Write in a conversational, natural tone. Make specific references to actual essay content and how it connects (or doesn't connect) to the candidate's background and INSEAD's expectations.

End with: "Consensus Rating: [1.0-5.0 in 0.5 increments]"

Most candidates rate 2.5-3.5. Excellent writing + realistic goals + clear INSEAD fit + strong coherence = 4.0-5.0.
`,

  step4EvaluationPrompt: `
STEP 4: FINAL HOLISTIC EVALUATION & ADMISSION PROBABILITY

Provide a comprehensive final assessment integrating all evaluation components for INSEAD MBA admission.

**OVERALL CANDIDATE SUMMARY:**
[Brief 2-3 sentence overview synthesizing the candidate's profile and fit]

**COMPONENT PERFORMANCE RECAP:**
- **Academics (Step 1)**: [Rating from 1-5] - [Brief strength/weakness summary]
- **Work Experience (Step 2)**: [Rating from 1-5] - [Brief strength/weakness summary]  
- **Essays & Goals (Step 3)**: [Rating from 1-5] - [Brief strength/weakness summary]

**INSEAD-SPECIFIC FIT ASSESSMENT:**
- **Global Mindset**: [Evidence of international perspective and cultural adaptability]
- **Diversity Contribution**: [Unique background factors that add value to INSEAD's community]
- **Entrepreneurial Spirit**: [Risk-taking, innovation, business creation potential]
- **Cultural Integration**: [Ability to thrive in INSEAD's multicultural, fast-paced environment]

**KEY STRENGTHS (3-4 items):**
- [Specific standout qualities that support admission]

**KEY CONCERNS (2-3 items):**
- [Specific areas of weakness or risk factors]

**ADMISSION PROBABILITY ASSESSMENT:**

**ACCEPT PROBABILITY: [X]%**
[Select appropriate range: 70-85% (Strong Accept), 55-70% (Likely Accept), 40-55% (Competitive Accept)]

**WAITLIST PROBABILITY: [Y]%**
[Select appropriate range: 10-30% depending on competitiveness]

**REJECT PROBABILITY: [Z]%**
[Select appropriate range: 5-45% depending on gaps]

**KEY REASONING FOR PROBABILITY:**
1. **[Primary Factor]**: [Most important factor driving the assessment]
2. **[Secondary Factor]**: [Second most important consideration]
3. **[Tertiary Factor]**: [Third key factor in decision]

**FINAL RECOMMENDATION:**
[ACCEPT/WAITLIST/REJECT]

**Final Committee Rating: [1.0-5.0 in 0.5 increments]**

**DECISION RATIONALE:**
[2-3 sentences explaining the final decision based on INSEAD's standards and the candidate's overall profile]

Note: Probabilities should reflect realistic INSEAD admission rates (~25% overall acceptance rate) and be consistent with component ratings.
`
};

// School information for display
export const INSEAD_INFO = {
  name: "INSEAD",
  fullName: "Institut Européen d'Administration des Affaires",
  description: "The Business School for the World",
  locations: ["France", "Singapore", "Abu Dhabi", "San Francisco"],
  programLength: "10 months",
  classSize: "~1000 students",
  averageGMAT: 710,
  averageGPA: 3.5,
  averageAge: 29,
  femalePercentage: 38,
  internationalPercentage: 95,
  website: "https://www.insead.edu"
}; 